import { cloneDeep, isArray, mergeWith } from "lodash";

import type { ApplicationModel } from "@com.mgmtp.a12.client/client-core/lib/core/model";

import { Manifest } from "../app/slices/manifest/types";

/***
 * Merges another application model into an existing application model.
 */
export function addApplicationModel(
	source: ApplicationModel,
	target: ApplicationModel,
	manifest?: Manifest,
	overrideHeader = false
): ApplicationModel {
	return {
		...target,
		header: overrideHeader ? { ...target.header, ...source.header } : { ...target.header },
		content: {
			...target.content,
			initialActivity: target.content.initialActivity || source.content.initialActivity,
			region: addRegions(source.content.region, target.content.region),
			modules: addModules(source.content.modules, target.content.modules, manifest)
		}
	};
}

function addModules(
	source: readonly ApplicationModel.Module[],
	target: readonly ApplicationModel.Module[],
	manifest?: Manifest
): readonly ApplicationModel.Module[] {
	const combinedModules: ApplicationModel.Module[] = [...target];
	for (const newModule of source) {
		// If we already have a module with this ID (name attribute), we will merge them together.
		const existingModuleIndex = combinedModules.findIndex(existingModule => existingModule.name === newModule.name);

		if (existingModuleIndex > -1) {
			const existingModule = combinedModules[existingModuleIndex];
			const updatedModule = cloneDeep(existingModule);
			const updatedMenu = mergeWith(
				updatedModule.menu,
				newModule.menu,
				mergeCustomizer(manifest?.identification.id)
			);
			const updatedFlows: ApplicationModel.Flow[] = [...cloneDeep(updatedModule.flows)];
			for (const newFlow of newModule.flows) {
				const existingFlowIndex = updatedFlows.findIndex(existingFlow => existingFlow.name === newFlow.name);
				if (existingFlowIndex > -1) {
					updatedFlows[existingFlowIndex] = mergeWith(
						updatedFlows[existingFlowIndex],
						newFlow,
						mergeCustomizer(manifest?.identification.id)
					);
				} else {
					updatedFlows.push(newFlow);
				}
			}

			combinedModules[existingModuleIndex] = {
				flows: updatedFlows,
				menu: updatedMenu,
				name: existingModule.name
			};
		} else {
			combinedModules.push(newModule);
		}
	}
	return combinedModules;
}

function addStringsToSet(source?: readonly string[], target?: readonly string[]): readonly string[] | undefined {
	// nothing to merge -> keep
	if (source === undefined || source.length === 0) {
		return target;
	}
	// nothing to merge into -> take over
	if (target === undefined || target.length === 0) {
		return source;
	}
	// merge new into existing
	const newStrings = source.filter(l => target.indexOf(l) < 0);
	return target.concat(newStrings);
}

function addRegionsIfPresent(
	source?: ApplicationModel.Region,
	target?: ApplicationModel.Region
): ApplicationModel.Region | undefined {
	// nothing to merge -> keep
	if (source === undefined) {
		return target;
	}
	// nothing to merge into -> take over
	if (target === undefined) {
		return source;
	}
	return addRegions(source, target);
}

function addRegions(source: ApplicationModel.Region, target: ApplicationModel.Region): ApplicationModel.Region {
	// merge all sub-regions
	const subRegions: ApplicationModel.Region[] = [];
	const regionNames = addStringsToSet(
		(source.subRegions ?? []).map(r => r.name),
		(target.subRegions ?? []).map(r => r.name)
	);
	for (const regionName of regionNames ?? []) {
		const mergedRegion = addRegionsIfPresent(
			source.subRegions?.find(r => r.name === regionName),
			target.subRegions?.find(r => r.name === regionName)
		);
		if (mergedRegion !== undefined) {
			subRegions.push(mergedRegion);
		}
	}
	return subRegions.length > 0 ? { ...target, subRegions } : target;
}

/**
 * For use with Lodash's mergeWith method. Makes arrays concatenate and prefixes scene IDs with the Fachverfahrens-Prefix if applicable.
 * This is necessary so that scene names are unique, otherwise the activity scene handling logic gets confused.
 * @param fachverfahrenId The ID of the Fachverfahren this appmodel belongs to, if any.
 */
const mergeCustomizer = (fachverfahrenId?: string) => (targetValue: any, srcValue: any) => {
	if (isArray(targetValue)) {
		if (srcValue?.every(isScene)) {
			return targetValue.concat(
				srcValue.map((it: ApplicationModel.Scene) => ({
					...it,
					name: fachverfahrenId ? `${fachverfahrenId}_${it.name}` : it.name
				}))
			);
		}
		return targetValue.concat(srcValue);
	}
	return undefined; // Use the standard merge behaviour
};

function isScene(scene: object): scene is ApplicationModel.Scene {
	return ["name", "matchConditions", "sceneChange"].every(it => it in scene);
}
